import React, { useMemo } from "react";
import { ShieldCheck, FileText } from "lucide-react";

/**
 * Renders the structured intelligence summary into styled section cards.
 * Falls back to <pre> if the content doesn't match the expected A-G template.
 */
export default function SummaryReport({ content }) {
  const parsed = useMemo(() => parseSummary(content), [content]);

  if (!parsed.sections.length) {
    return (
      <pre className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm text-slate-800 whitespace-pre-wrap font-mono leading-relaxed">
        {content}
      </pre>
    );
  }

  return (
    <div className="space-y-3" data-testid="summary-report">
      <div className="flex items-center gap-2 px-4 py-3 bg-slate-900 text-white rounded-sm">
        <ShieldCheck className="h-5 w-5 text-amber-300" />
        <h3 className="text-sm font-bold tracking-tight">
          {parsed.title || "LAPORAN EKSTRAKSI INTELIJEN MILITER & KEAMANAN"}
        </h3>
      </div>
      <ol className="space-y-2.5">
        {parsed.sections.map((s, idx) => {
          const isEmpty = /tidak ditemukan data terkait/i.test(s.body);
          return (
            <li
              key={idx}
              className={`border rounded-sm overflow-hidden ${
                isEmpty ? "border-slate-200 bg-slate-50" : "border-amber-200 bg-amber-50/40"
              }`}
              data-testid={`summary-section-${s.letter}`}
            >
              <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-white">
                <span className={`inline-flex h-6 w-6 items-center justify-center rounded-sm font-mono text-xs font-bold ${
                  isEmpty ? "bg-slate-200 text-slate-600" : "bg-amber-500 text-white"
                }`}>
                  {s.letter}
                </span>
                <p className="text-xs uppercase tracking-wider font-semibold text-slate-700">{s.title}</p>
              </div>
              <p className={`px-4 py-3 text-sm leading-relaxed ${isEmpty ? "text-slate-500 italic" : "text-slate-800"}`}>
                <FileText className={`inline h-3.5 w-3.5 mr-1 mb-0.5 ${isEmpty ? "text-slate-400" : "text-amber-700"}`} />
                {s.body}
              </p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * Parse the strict template into { title, sections: [{ letter, title, body }] }.
 * Robust to slight whitespace/format drift from the LLM.
 */
function parseSummary(content) {
  if (!content) return { title: "", sections: [] };
  const lines = content.split(/\r?\n/);
  let title = "";
  const sections = [];

  // Look for the first non-empty line containing emoji or "LAPORAN"
  for (const line of lines) {
    if (/LAPORAN/i.test(line)) {
      title = line.replace(/^[^A-Za-z]+/, "").trim();
      break;
    }
  }

  // Match lines like "A. INSTITUSI..." or "B. KOMANDO..."
  const sectionRegex = /^([A-G])\.\s+(.+?)\s*$/;
  let current = null;
  for (const line of lines) {
    const m = line.match(sectionRegex);
    if (m) {
      if (current) sections.push(current);
      current = { letter: m[1], title: m[2].trim(), body: "" };
      continue;
    }
    if (current) {
      // Skip the "- Ringkasan temuan:" prefix
      const cleaned = line
        .replace(/^\s*[-•]\s*/, "")
        .replace(/^Ringkasan temuan\s*:\s*/i, "")
        .trim();
      if (cleaned) {
        current.body = current.body ? current.body + " " + cleaned : cleaned;
      }
    }
  }
  if (current) sections.push(current);

  // Filter out spurious sections (no body and no recognized title)
  const valid = sections.filter((s) => s.title.length > 0);
  return { title, sections: valid };
}
